/**
 * @fileoverview "Power banks" on the brand ambassador profile: every unit
 * currently out with them, the hold on it, and the two things a city manager
 * can do about it - mark it returned (hold released) or write it off as lost
 * or broken (hold kept by the company). Both go through a confirmation that
 * spells out what happens to the money.
 */
import React, { useCallback, useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, Row, Col } from 'react-bootstrap'
import styled from 'styled-components'

import { AmbassadorSection } from 'Components/City/Page/Ambassador/AmbassadorSection'
import { SectionTitle } from 'Components/Section/SectionTitle'
import { Table, Th, Tr, Td } from 'Components/Table'
import SubmitButton from 'Components/Form/SubmitButton'
import LoadingIndicator from 'Components/LoadingIndicator/LoadingIndicator'
import { SnackBarContext } from 'Components/SnackBar'
import PowerBankDialog from 'Components/PowerBanks/PowerBankDialog'
import { readGraphQLData, useCityCurrency } from 'Components/PowerBanks/hooks'
import {
  getAmbassadorPowerBanks,
  returnPowerBank,
  writeOffPowerBank,
} from 'Module/graphQL'
import {
  describeUnitAgreement,
  describeUnitHold,
  formatHoldAmount,
  sortUnitsBySerial,
  unitHasLiveHold,
} from 'Utils/PowerBanks'

const Totals = styled.div`
  padding: 0 40px 24px 40px;
  font-family: 'Proxima Nova', sans-serif;
  font-size: 14px;
  color: #4a5568;

  strong {
    color: #011533;
  }

  span + span::before {
    content: '·';
    margin: 0 10px;
    color: #9f9f9f;
  }
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`

const LoadError = styled.div`
  padding: 0 40px 40px 40px;
  color: #c8102e;
  font-size: 14px;
`

const ACTION_RETURN = 'return'
const ACTION_WRITE_OFF = 'writeOff'

const unitLabel = unit => unit.description || `Power bank #${unit.inventoryId}`

const EMPTY_TOTALS = {
  heldAmount: 0,
  releasedAmount: 0,
  cancelledAmount: 0,
  activeCount: 0,
}

const PowerBanksSection = ({ ambassador }) => {
  const ambassadorId = ambassador.id
  const cityId = ambassador.city ? ambassador.city.id : null
  const ambassadorName = `${ambassador.firstName || ''} ${ambassador.lastName ||
    ''}`.trim()

  const runSnackBar = useContext(SnackBarContext)
  const { money } = useCityCurrency(cityId)

  const [units, setUnits] = useState([])
  const [totals, setTotals] = useState(EMPTY_TOTALS)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [action, setAction] = useState(null)
  const [actionUnit, setActionUnit] = useState(null)

  const load = useCallback(async () => {
    if (!ambassadorId) return
    setLoading(true)
    setLoadError(null)
    try {
      const data = await readGraphQLData(
        await getAmbassadorPowerBanks({
          ambassadorId,
          cityId,
          holdStatus: null,
        }),
      )
      const payload = data.ambassadorPowerBanks || {}
      // The unit list carries the live hold id; the hold list carries whether
      // the BA agreed to it in the app. Join them here for the table.
      const acceptedAtByHoldId = {}
      ;(payload.holds || []).forEach(hold => {
        acceptedAtByHoldId[String(hold.id)] = hold.acceptedAt || null
      })
      setUnits(
        sortUnitsBySerial(
          (payload.units || []).map(unit => ({
            ...unit,
            holdAcceptedAt: unit.holdId
              ? acceptedAtByHoldId[String(unit.holdId)] || null
              : null,
          })),
        ),
      )
      setTotals({ ...EMPTY_TOTALS, ...(payload.totals || {}) })
    } catch (error) {
      setLoadError(error.message || 'The power banks could not be loaded.')
    } finally {
      setLoading(false)
    }
  }, [ambassadorId, cityId])

  useEffect(() => {
    load()
  }, [load])

  const openAction = useCallback((name, unit) => {
    setAction(name)
    setActionUnit(unit)
  }, [])

  const closeAction = useCallback(() => {
    setAction(null)
    setActionUnit(null)
  }, [])

  const onReturnConfirm = useCallback(async () => {
    const data = await readGraphQLData(
      await returnPowerBank({
        inventoryId: actionUnit.inventoryId,
        ambassadorId,
        cityId,
        comments: 'Returned on the brand ambassador profile.',
      }),
    )
    const hold = data.returnPowerBank && data.returnPowerBank.hold
    const releasedMoney = hold && Number(hold.amount) > 0
    closeAction()
    runSnackBar({
      type: 'success',
      msg: releasedMoney
        ? `${unitLabel(actionUnit)} returned, hold of ${formatHoldAmount(
            hold.amount,
            { currencySign: money.currencySign, currency: hold.currency },
          )} released to ${ambassadorName}`
        : `${unitLabel(actionUnit)} returned`,
    })
    await load()
  }, [
    actionUnit,
    ambassadorId,
    cityId,
    closeAction,
    runSnackBar,
    money,
    ambassadorName,
    load,
  ])

  const onWriteOffConfirm = useCallback(async () => {
    const data = await readGraphQLData(
      await writeOffPowerBank({
        inventoryId: actionUnit.inventoryId,
        ambassadorId,
        cityId,
        comments:
          'Written off as lost or broken on the brand ambassador profile.',
      }),
    )
    const hold = data.writeOffPowerBank && data.writeOffPowerBank.hold
    const keptMoney = hold && Number(hold.amount) > 0
    closeAction()
    runSnackBar({
      type: 'success',
      msg: keptMoney
        ? `${unitLabel(actionUnit)} written off, hold of ${formatHoldAmount(
            hold.amount,
            { currencySign: money.currencySign, currency: hold.currency },
          )} kept by the company`
        : `${unitLabel(actionUnit)} written off`,
    })
    await load()
  }, [actionUnit, ambassadorId, cityId, closeAction, runSnackBar, money, load])

  const hasHistory =
    Number(totals.releasedAmount) > 0 || Number(totals.cancelledAmount) > 0

  return (
    <AmbassadorSection className="no-padding">
      <Grid fluid>
        <Row className="padding-row">
          <Col xs={12}>
            <SectionTitle>Power banks</SectionTitle>
          </Col>
        </Row>
        {loading && (
          <Row>
            <Col xs={12}>
              <LoadingIndicator />
            </Col>
          </Row>
        )}
        {!loading && loadError && (
          <LoadError role="alert">
            {loadError}{' '}
            <SubmitButton
              value="Retry"
              size="small"
              variant="outlined"
              onClick={load}
            />
          </LoadError>
        )}
        {!loading && !loadError && units.length === 0 && (
          <p className="not-found">No power banks held</p>
        )}
        {!loading && !loadError && units.length > 0 && (
          <Row>
            <Col xs={12} className="full-width-table">
              <Table>
                <thead>
                  <Tr>
                    <Th className="col-xs-3">Serial number</Th>
                    <Th className="centered col-xs-2">Hold</Th>
                    <Th className="centered col-xs-3">BA agreement</Th>
                    <Th className="align-right">Actions</Th>
                  </Tr>
                </thead>
                <tbody>
                  {units.map(unit => (
                    <Tr key={unit.inventoryId}>
                      <Td className="col-xs-3">
                        <p>{unitLabel(unit)}</p>
                      </Td>
                      <Td className="centered col-xs-2">
                        <p>{describeUnitHold(unit, money)}</p>
                      </Td>
                      <Td className="centered col-xs-3">
                        <p>{describeUnitAgreement(unit)}</p>
                      </Td>
                      <Td className="align-right">
                        <Actions>
                          <SubmitButton
                            value="Returned"
                            size="small"
                            variant="outlined"
                            onClick={() => openAction(ACTION_RETURN, unit)}
                          />
                          <SubmitButton
                            value="Lost / broken"
                            size="small"
                            variant="outlined"
                            onClick={() => openAction(ACTION_WRITE_OFF, unit)}
                          />
                        </Actions>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </Col>
          </Row>
        )}
        {!loading && !loadError && (units.length > 0 || hasHistory) && (
          <Totals>
            <span>
              Held now:{' '}
              <strong>{formatHoldAmount(totals.heldAmount, money)}</strong>
            </span>
            <span>
              Released:{' '}
              <strong>{formatHoldAmount(totals.releasedAmount, money)}</strong>
            </span>
            <span>
              Kept by company:{' '}
              <strong>{formatHoldAmount(totals.cancelledAmount, money)}</strong>
            </span>
          </Totals>
        )}
      </Grid>

      {action === ACTION_RETURN && actionUnit && (
        <PowerBankDialog
          show
          title="Power bank returned"
          confirmLabel="Mark as returned"
          onConfirm={onReturnConfirm}
          onCancel={closeAction}
        >
          <p>
            Mark <strong>{unitLabel(actionUnit)}</strong> as returned by{' '}
            <strong>{ambassadorName}</strong>?
          </p>
          {unitHasLiveHold(actionUnit) ? (
            <p>
              The hold of <strong>{describeUnitHold(actionUnit, money)}</strong>{' '}
              is released back to the brand ambassador.
            </p>
          ) : (
            <p>There is no hold on this power bank, no money changes hands.</p>
          )}
        </PowerBankDialog>
      )}

      {action === ACTION_WRITE_OFF && actionUnit && (
        <PowerBankDialog
          show
          title="Power bank lost or broken"
          confirmLabel="Write off"
          onConfirm={onWriteOffConfirm}
          onCancel={closeAction}
        >
          <p>
            Write off <strong>{unitLabel(actionUnit)}</strong> held by{' '}
            <strong>{ambassadorName}</strong> as lost or broken?
          </p>
          {unitHasLiveHold(actionUnit) ? (
            <p>
              The hold of <strong>{describeUnitHold(actionUnit, money)}</strong>{' '}
              is cancelled and <strong>kept by the company</strong>. It is not
              given back to the brand ambassador.
            </p>
          ) : (
            <p>There is no hold on this power bank, no money changes hands.</p>
          )}
          <p>
            The unit is removed from stock. The brand ambassador can still be
            given another power bank.
          </p>
        </PowerBankDialog>
      )}
    </AmbassadorSection>
  )
}

PowerBanksSection.propTypes = {
  ambassador: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    city: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }).isRequired,
}

export default PowerBanksSection
